
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuthToken, removeAuthToken } from "@/utils/storage";
import { clearTeamsPayloadCache } from "@/hooks/use-teams-page-payload";

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTheme } from "next-themes";
import { Sun, Moon, Shield, Heart } from "lucide-react";
import { BrandLogo } from "./nav/brand-logo";
import { DesktopNav } from "./nav/desktop-nav";
import { MobileNav } from "./nav/mobile-nav";
import { usePendingInvites } from "@/hooks/use-pending-invites";
import { useUserRole } from "@/hooks/use-user-role";
import { useGuardianStatus } from "@/hooks/use-guardian-status";
import { useCCTeamRole } from "@/hooks/use-cc-team-roles";
import { PWARefreshButton } from "./pwa-refresh-button";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { LanguagePicker } from "@/components/i18n/language-picker";
import { useLanguage } from "@/contexts/language-context";
import { useViewAsParent, supportsInPageParentView } from "@/hooks/use-view-as-parent";

interface HeaderProps {
  className?: string;
}

const AUTH_CHECK_TIMEOUT_MS = 2_500;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | null> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => resolve(null), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

export function Header({ className = '' }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, setTheme } = useTheme();
  const { t } = useLanguage();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [hasTeams, setHasTeams] = useState(true);
  const { invitesCount } = usePendingInvites();
  const { isAdmin } = useUserRole();
  const { isGuardian } = useGuardianStatus();
  const { hasCCRole, ccRoleLabel } = useCCTeamRole();

  const isParentView = location.pathname.startsWith('/parent');
  const { viewAsParent, toggle: toggleViewAsParent } = useViewAsParent();
  const inPageParentView = supportsInPageParentView(location.pathname);
  const showingParent = isParentView || (inPageParentView && viewAsParent);
  const isDualRole = isGuardian && (hasTeams || hasCCRole);
  const showModeToggle = isLoggedIn && isDualRole;
  const managementRoleLabel = ccRoleLabel || (hasTeams ? "Coach" : "Manager");

  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        setIsCheckingAuth(true);
        
        // Get the current session from Supabase
        const result = await withTimeout(supabase.auth.getSession(), AUTH_CHECK_TIMEOUT_MS);
        const session = result?.data?.session ?? null;
        const error = result?.error ?? null;

        if (!result) {
          console.warn("Session check timed out in header");
        }
        
        if (error) {
          console.error("Session error:", error);
          // Clear auth data if there's a session error
          removeAuthToken();
          setIsLoggedIn(false);
          setUserEmail(null);
          setIsCheckingAuth(false);
          return;
        }
        
        if (session?.user?.email) {
          // Valid session found
          const email = session.user.email;
          setUserEmail(email);
          setIsLoggedIn(true);
          
          // Check if user has teams (coach)
          const { data: teamRows } = await supabase
            .from("teamvote_teams")
            .select("id")
            .eq("coach_id", session.user.id)
            .limit(1);
          
          // Also check team_access for collaborator role
          const { data: accessRows } = await (supabase as any)
            .from("team_access")
            .select("id")
            .eq("email", session.user.email || "")
            .eq("status", "active")
            .limit(1);
          
          setHasTeams((teamRows && teamRows.length > 0) || (accessRows && accessRows.length > 0));
        } else {
          // No valid session - not logged in
          removeAuthToken(); // Ensure we clear any stale token
          setIsLoggedIn(false);
          setUserEmail(null);
        }
      } catch (err) {
        console.error("Error checking auth:", err);
        setIsLoggedIn(false);
        setUserEmail(null);
        removeAuthToken(); // Ensure we clear any stale token
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    fetchUserEmail();
    
    // Set up auth change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event);
        
        if (event === 'SIGNED_IN' && session?.user?.email) {
          setUserEmail(session.user.email);
          setIsLoggedIn(true);
        } else if (event === 'SIGNED_OUT') {
          removeAuthToken(); // Ensure token is removed on signout
          clearTeamsPayloadCache(); // Never leave one account's teams for the next
          setUserEmail(null);
          setIsLoggedIn(false);

        }
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (hasCCRole) setHasTeams(true);
  }, [hasCCRole]);

  const handleLogout = async () => {
    try {
      setIsCheckingAuth(true);
      await supabase.auth.signOut();
      removeAuthToken();
      setUserEmail(null);
      setIsLoggedIn(false);
      
      // Clear caches on logout
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName)
              .then(() => console.log(`Cache ${cacheName} cleared on logout`))
              .catch(err => console.error(`Failed to clear cache ${cacheName}:`, err));
          });
        });
      }
      
      navigate("/");
      toast.success(t('toasts.signedOut'));
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error(t('toasts.signOutFailed'));
    } finally {
      setIsCheckingAuth(false);
    }
  };

  return (
    <header className={`sticky top-0 z-50 w-full backdrop-blur-lg bg-background/80 border-b ${className}`}>
      <div className="max-container flex min-h-14 md:h-16 items-center justify-between gap-2 min-w-0 py-1 md:py-0">
        <BrandLogo size="medium" />

        <div className="flex items-center gap-0.5 sm:gap-1 md:gap-2 shrink-0">
          <LanguagePicker />

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
            className="h-9 w-9"
            aria-label={t('settings.themeLabel')}
          >
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>

          {showModeToggle && (
            // On desktop in coach view, the labeled "My Kids" link in DesktopNav
            // already points to /parent — hide this toggle there to avoid duplication.
            // Keep it on mobile (My Kids lives in the hamburger) and on the parent
            // route (so users can switch back to coach mode).
            // On a match page (/rsvp/:id, /game/:id) the toggle flips the view of
            // THIS page instead of navigating away to the parent dashboard.
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    if (inPageParentView) toggleViewAsParent();
                    else navigate(isParentView ? '/teams' : '/parent');
                  }}
                  className={`inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors ${(isParentView || inPageParentView) ? '' : 'md:hidden'} ${inPageParentView && viewAsParent ? 'text-primary' : ''}`}
                  aria-label={showingParent ? t('modeToggle.switchToCoach', { role: managementRoleLabel }) : t('modeToggle.switchToParent')}
                >
                  {showingParent ? (
                    <Shield className="h-4 w-4" />
                  ) : (
                    <Heart className="h-4 w-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {showingParent ? t('modeToggle.switchToCoach', { role: managementRoleLabel }) : t('modeToggle.switchToParent')}
              </TooltipContent>
            </Tooltip>
          )}

          
          <PWARefreshButton />
          
          <DesktopNav 
            isLoggedIn={isLoggedIn}
            userEmail={userEmail}
            isAdmin={isAdmin}
            handleLogout={handleLogout}
            invitesCount={invitesCount}
            isGuardian={isGuardian}
            pendingRsvpCount={0}
            hasTeams={hasTeams}
            isParentView={isParentView}
            isDualRole={isDualRole}
            managementRoleLabel={managementRoleLabel}
          />

          <MobileNav 
            isLoggedIn={isLoggedIn}
            userEmail={userEmail}
            isAdmin={isAdmin}
            handleLogout={handleLogout}
            invitesCount={invitesCount}
            isGuardian={isGuardian}
            pendingRsvpCount={0}
            hasTeams={hasTeams}
            isParentView={isParentView}
            isDualRole={isDualRole}
            managementRoleLabel={managementRoleLabel}
          />
        </div>
      </div>
    </header>
  );
}
